import { RubyGemMark } from '../components/RubyGemMark';
import { RubyLoader } from '../components/RubyLoader';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Percent, Trash2, AlertTriangle, Check, X, Copy, Search, ArrowLeft,
} from 'lucide-react';
import { billingApi, type CommissionPlanRow, type CommissionRuleRow } from '../services/billingApi';
import * as sharedTypes from '@platform/shared-types';
import SearchableDropdown from '../components/SearchableDropdown';
import { api } from '../services/apiService';
import { hardwareApi, type DeviceBundleRow, type DeviceModelRow } from '../services/hardwareApi';
import type { BillingSkuRow } from '../services/billingApi';
import ShimmerImage from '../components/ShimmerImage';
import { getIcon } from '../services/iconRegistry';
import { resolveAccent, tint } from '../utils/accentColor';
import { money } from '../components/finance/financeFormat';
import { InventoryPage, RUBY, RUBY_DEEP } from './inventory/InventoryChrome';

/**
 * ONE COMMISSION PLAN — `/commission-plans/:planId`, and `/commission-plans/new`.
 *
 * Approved design: `mockups/partner-commission-plans.html` (screens 2 and 3).
 * Split out of `CommissionPlansPage` on 2026-08-30 when that screen became a
 * table; the editor itself is unchanged in behaviour and now has the full page
 * width its target pickers always needed.
 *
 * A plan is a list of RULES, laid out the same way the deal builder lays out
 * its discount structure: a row per thing, a rate on each. This screen is the
 * ONE place rates are edited — a deal points at a plan rather than carrying
 * three loose commission fields of its own.
 *
 * WHAT THIS SCREEN DELIBERATELY DOES NOT DO
 * -----------------------------------------
 * It does not compute what a rule would pay. That projection now exists — it
 * is `previewPlanEarnings` in shared-types — but it needs a DEAL's prices to
 * mean anything, so it lives on the deal builder and the partner portal, where
 * those prices are. If a preview is ever added here it MUST call the same
 * shared-types functions the pay path uses rather than re-deriving the
 * arithmetic, or an operator would price against numbers nothing downstream
 * agrees with.
 *
 * `version` is server-owned and read-only here: it bumps only when rules
 * change, so a commission event's `planVersion` keeps pointing at the rates
 * that actually paid it.
 */

/**
 * GUARDED RUNTIME IMPORTS. A stale Vite pre-bundle resolves a newly-added
 * shared-types export to `undefined`, and calling one at module scope
 * white-screens the page. Every VALUE this file uses is read through the
 * namespace and checked; the remedy (restart the dev server — deleting
 * `node_modules/.vite` under a running one does nothing) is named in the log.
 */
const targetsOf: (r: any) => string[] = typeof sharedTypes.targetsOf === 'function'
  ? sharedTypes.targetsOf
  : (r: any) => Array.from(new Set([r?.target, ...(r?.targets || [])].filter(Boolean)));
const slugifyPlanId: (n: string) => string = typeof sharedTypes.slugifyPlanId === 'function'
  ? sharedTypes.slugifyPlanId
  : () => '';
if (typeof sharedTypes.targetsOf !== 'function' || typeof sharedTypes.slugifyPlanId !== 'function') {
  console.error(
    '[CommissionPlanEditPage] @platform/shared-types resolved to a stale bundle — '
    + 'RESTART the dev server (deleting node_modules/.vite under a running server does nothing). '
    + 'Target matching and the plan-id preview are degraded until you do.',
  );
}

/**
 * The scopes an operator edits.
 *
 * `subscription` and `merchant_processing` are DELIBERATELY NOT HERE.
 *
 * A deal's monthly price IS its packages — that is what `dealPackagesTotals`
 * composes it from. So a plan carrying both a per-package rule and a
 * `subscription` rule pays a partner TWICE on the same money: once for the
 * packages, once for the subscription those packages add up to. Pricing per
 * package is the finer-grained control and the one that lets a specific
 * package be boosted, so that is the one kept.
 *
 * `merchant_processing` residuals are the ISO track's, not this platform's
 * billing, and nothing emits an event for them yet.
 *
 * Both remain VALID in the schema — a stored rule using either still parses and
 * would still pay. They are only removed from the editor. Any plan that already
 * carries one is surfaced below rather than hidden, because a rule that pays
 * money while being invisible is exactly the trap the deal page's old
 * commission fields set.
 */
const SCOPES: { id: CommissionRuleRow['scope']; label: string; hint: string }[] = [
  { id: 'device_offer', label: 'Devices & bundles', hint: 'paid when the sale settles' },
  { id: 'software_package', label: 'Software packages', hint: 'paid per billing cycle — price each package to boost the ones you want sold' },
];

/** Scopes no longer edited here, but still payable if a stored rule uses one. */
const RETIRED_SCOPES: CommissionRuleRow['scope'][] = ['subscription', 'merchant_processing'];

const BASIS_LABEL: Record<CommissionRuleRow['basis'], string> = {
  percent_of_price: '% of price',
  percent_of_margin: '% of margin',
  flat_per_unit: 'Flat per unit',
};

const ANY = '*';

const blankRule = (scope: CommissionRuleRow['scope']): CommissionRuleRow => ({
  // Random suffix, never re-derived: a rule id is written onto every event it
  // pays, so regenerating one would orphan that history.
  ruleId: `rule_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`,
  scope,
  target: ANY,
  basis: scope === 'device_offer' ? 'percent_of_margin' : 'percent_of_price',
  rate: 10,
  flatCents: 0,
  recurrence: scope === 'device_offer' ? 'once' : 'monthly',
  months: scope === 'device_offer' ? null : 12,
});

const centsToStr = (c?: number) => (typeof c === 'number' && c > 0 ? (c / 100).toFixed(2) : '');
const strToCents = (s: string) => {
  const n = Number.parseFloat(s);
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : 0;
};

/** One selectable thing a rule can point at, with something to look at. */
interface TargetChoice {
  id: string;
  label: string;
  subtitle?: string;
  /** Packages carry a lucide icon name; devices carry a photo. */
  iconName?: string;
  imageUrl?: string;
  accent?: any;
}

/**
 * TARGET PICKER — multi-select, with the icon or photo of the thing.
 *
 * A rule usually applies to a SET ("15% on all three compliance packages"), and
 * a single-value dropdown forces that into three near-identical rules whose
 * rates then drift apart. Selecting several here writes ONE rule covering all
 * of them (`target` + `targets`).
 *
 * Given its own full-width row rather than a grid cell: package names are long,
 * and a truncated name is how the wrong thing gets a commission attached to it.
 */
const TargetPicker: React.FC<{
  choices: TargetChoice[];
  selected: string[];
  onChange: (ids: string[]) => void;
  /** Ids already claimed by another rule in this scope — shown, but unpickable. */
  claimed: Set<string>;
  catchAllLabel: string;
}> = ({ choices, selected, onChange, claimed, catchAllLabel }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');

  const isCatchAll = selected.length === 1 && selected[0] === ANY;
  const byId = new Map(choices.map((c) => [c.id, c]));
  const chosen = selected.filter((id) => id !== ANY);

  const toggle = (id: string) => {
    // Catch-all and specific targets are mutually exclusive: a rule that means
    // "everything" cannot also mean "these three".
    if (selected.includes(id)) onChange(selected.filter((x) => x !== id));
    else onChange([...selected.filter((x) => x !== ANY), id]);
  };

  const visible = choices.filter((c) => {
    const needle = q.trim().toLowerCase();
    if (!needle) return true;
    return c.label.toLowerCase().includes(needle) || c.id.toLowerCase().includes(needle);
  });

  const Thumb: React.FC<{ c: TargetChoice; size: number }> = ({ c, size }) => {
    if (c.imageUrl !== undefined) {
      return (
        <ShimmerImage
          src={c.imageUrl}
          alt={c.label}
          width={size}
          height={size}
          objectFit="contain"
          className="shrink-0 rounded-md border border-stone-200 bg-stone-50"
          fallbackBgColor="bg-stone-100"
          fallbackIcon={<Percent size={size * 0.45} className="text-stone-400" />}
          variant="light"
        />
      );
    }
    const Icon = getIcon(c.iconName || '');
    const a = c.accent ? resolveAccent(c.accent) : null;
    return (
      <div
        className="flex shrink-0 items-center justify-center rounded-md border border-stone-200"
        style={{ width: size, height: size, background: a ? tint(a.from, 0.14) : '#f5f5f4' }}
      >
        <Icon size={size * 0.5} style={{ color: a ? a.from : '#78716c' }} />
      </div>
    );
  };

  return (
    <div>
      <label className="mb-1 block text-[10px] font-semibold text-stone-500">Applies to</label>

      <div className="rounded-lg border border-stone-300 bg-white p-2">
        {isCatchAll || selected.length === 0 ? (
          <div className="flex items-center gap-2 px-1 py-0.5 text-xs text-stone-500">
            <span className="rounded bg-stone-100 px-1.5 py-0.5 font-semibold text-stone-600">{catchAllLabel}</span>
            <span className="text-[10px]">applies to anything no other rule names</span>
          </div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {chosen.map((id) => {
              const c = byId.get(id);
              return (
                <span key={id} className="inline-flex max-w-full items-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50 py-1 pl-1 pr-1.5">
                  {c ? <Thumb c={c} size={20} /> : null}
                  <span className="truncate text-[11px] font-medium text-stone-800">{c?.label || id}</span>
                  <button
                    onClick={() => toggle(id)}
                    className="shrink-0 rounded p-0.5 text-stone-400 hover:text-red-600"
                    title="Remove"
                  >
                    <X size={11} />
                  </button>
                </span>
              );
            })}
          </div>
        )}

        <div className="mt-2 flex items-center gap-2 border-t border-stone-100 pt-2">
          <button
            onClick={() => setOpen((v) => !v)}
            className="rounded-lg border border-stone-300 px-2.5 py-1 text-[11px] font-semibold text-stone-600 hover:border-rose-300 hover:text-rose-600"
          >
            {open ? 'Done' : '+ Choose specific'}
          </button>
          {!isCatchAll && selected.length > 0 && (
            <button
              onClick={() => onChange([ANY])}
              className="text-[10px] text-stone-500 underline decoration-dotted hover:text-rose-600"
            >
              use {catchAllLabel.toLowerCase()} instead
            </button>
          )}
          {chosen.length > 0 && (
            <span className="ml-auto text-[10px] text-stone-400">{chosen.length} selected</span>
          )}
        </div>

        {open && (
          <div className="mt-2 rounded-lg border border-stone-200">
            <div className="relative border-b border-stone-100 p-1.5">
              <Search size={12} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search…"
                className="w-full rounded-md border border-stone-200 py-1.5 pl-7 pr-2 text-[11px]"
              />
            </div>
            <div className="max-h-56 overflow-y-auto p-1">
              {visible.length === 0 && (
                <p className="p-3 text-center text-[11px] text-stone-400">Nothing matches.</p>
              )}
              {visible.map((c) => {
                const on = selected.includes(c.id);
                const blocked = !on && claimed.has(c.id);
                return (
                  <button
                    key={c.id}
                    disabled={blocked}
                    onClick={() => toggle(c.id)}
                    className={`flex w-full items-center gap-2 rounded-md p-1.5 text-left transition ${
                      blocked ? 'cursor-not-allowed opacity-40'
                        : on ? 'bg-rose-50' : 'hover:bg-stone-50'
                    }`}
                  >
                    <Thumb c={c} size={28} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[11.5px] font-medium text-stone-800">{c.label}</span>
                      <span className="block truncate text-[10px] text-stone-500">
                        {blocked ? 'already used by another rule in this section' : (c.subtitle || c.id)}
                      </span>
                    </span>
                    {on && <Check size={13} className="shrink-0 text-rose-600" />}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const CommissionPlanEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { planId: routePlanId } = useParams<{ planId: string }>();
  const isNew = !routePlanId || routePlanId === 'new';

  const [plan, setPlan] = useState<CommissionPlanRow | null>(null);
  const [draft, setDraft] = useState<CommissionPlanRow | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);
  /** Ids already in use — only to preview the id a new plan will be given. */
  const [takenIds, setTakenIds] = useState<Set<string>>(new Set());

  /**
   * What a rule can point AT. Loaded so a target is BROWSED, never typed: an
   * operator should not have to know that a package id is `pkg_pos_compliance`,
   * and a typo'd target silently matches nothing and pays nobody.
   * All non-fatal — a rule can still be edited if a catalog is unreachable.
   */
  const [packages, setPackages] = useState<any[]>([]);
  const [deviceSkus, setDeviceSkus] = useState<BillingSkuRow[]>([]);
  const [models, setModels] = useState<DeviceModelRow[]>([]);
  const [bundles, setBundles] = useState<DeviceBundleRow[]>([]);

  const load = useCallback(async () => {
    if (isNew) {
      const now = Date.now();
      setDraft({
        planId: '', name: '', status: 'draft', version: 1, isDefault: false,
        rules: [blankRule('device_offer')], createdAt: now, updatedAt: now,
      });
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await billingApi.getCommissionPlan(routePlanId as string);
      setPlan(res.plan);
      setDraft(null);
    } catch (e: any) {
      setError(e?.message || 'Could not load this commission plan');
    } finally {
      setLoading(false);
    }
  }, [isNew, routePlanId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let cancelled = false;
    // Each catalog is fetched independently: one being down must not blank the
    // other two pickers.
    api.packages.listVisible()
      .then((r: any) => { if (!cancelled) setPackages(r?.packages || []); })
      .catch(() => { /* leave empty — the picker says so */ });
    billingApi.listSkus()
      .then((r) => { if (!cancelled) setDeviceSkus((r.items || []).filter((k) => k.type === 'device_finance')); })
      .catch(() => { /* ignore */ });
    hardwareApi.listModels()
      .then((r) => { if (!cancelled) setModels(r.items || []); })
      .catch(() => { /* ignore */ });
    hardwareApi.listBundles()
      .then((r) => { if (!cancelled) setBundles(r.items || []); })
      .catch(() => { /* ignore */ });
    // Only to PREVIEW the id a new plan will get — the server mints the real
    // one from the same rule, against the table rather than this snapshot.
    if (isNew) {
      billingApi.listCommissionPlans()
        .then((r) => { if (!cancelled) setTakenIds(new Set((r.items || []).map((p) => p.planId))); })
        .catch(() => { /* the preview simply shows the un-suffixed slug */ });
    }
    return () => { cancelled = true; };
  }, [isNew]);

  const selected = draft || plan;
  const dirty = !!draft && !isNew;

  const patchDraft = (patch: Partial<CommissionPlanRow>) => {
    if (!selected) return;
    setDraft({ ...selected, ...patch });
  };

  const patchRule = (ruleId: string, patch: Partial<CommissionRuleRow>) => {
    if (!selected) return;
    patchDraft({ rules: selected.rules.map((r) => (r.ruleId === ruleId ? { ...r, ...patch } : r)) });
  };

  const addRule = (scope: CommissionRuleRow['scope']) => {
    if (!selected) return;
    patchDraft({ rules: [...selected.rules, blankRule(scope)] });
  };

  const removeRule = (ruleId: string) => {
    if (!selected) return;
    patchDraft({ rules: selected.rules.filter((r) => r.ruleId !== ruleId) });
  };

  /**
   * The id this plan WILL be given, previewed as the name is typed.
   *
   * Shown rather than hidden because the id is the string that ends up in
   * every commission event and in every payment investigation, and it is
   * frozen the moment the plan is first saved — so it is worth seeing before
   * that happens rather than after. The server mints it by the same rule
   * (`uniquePlanId`) against the live table, so the suffix here is a
   * prediction, not a promise.
   */
  const mintedIdPreview = useMemo(() => {
    if (!isNew || !selected?.name?.trim()) return '';
    const base = slugifyPlanId(selected.name);
    if (!base) return '';
    if (!takenIds.has(base)) return base;
    for (let n = 2; n < 100; n += 1) if (!takenIds.has(`${base}-${n}`)) return `${base}-${n}`;
    return base;
  }, [isNew, selected?.name, takenIds]);

  const choicesFor = (scope: CommissionRuleRow['scope']): TargetChoice[] => {
    if (scope === 'software_package') {
      return packages
        .map((pk: any) => ({
          id: pk.id,
          label: pk.name || pk.id,
          subtitle: typeof pk.basePrice === 'number' && pk.basePrice > 0
            ? `lists ${money(pk.basePrice)}/mo${pk.category ? ` · ${pk.category}` : ''}`
            : (pk.category || 'no list price set'),
          iconName: pk.icon || '',
          accent: pk,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
    if (scope === 'device_offer') {
      const modelRows: TargetChoice[] = models.map((m) => ({
        id: m.modelId,
        label: m.modelName,
        subtitle: `Device · landed ${money(m.landedCostCents)} — covers every offer on it`,
        imageUrl: m.media?.heroImageUrl,
      }));
      // Archived bundles (`status === 'eol'`) are no longer sold, so a NEW
      // commission rule can't be pointed at one. Rules already written against
      // an archived bundle are stored by id and keep paying out untouched.
      const bundleRows: TargetChoice[] = bundles.filter((b) => b.status !== 'eol').map((b) => ({
        id: b.bundleId,
        label: b.name,
        subtitle: `Bundle · landed ${money(b.componentsLandedCents)}`,
        imageUrl: b.heroImageUrl,
      }));
      const skuRows: TargetChoice[] = deviceSkus.map((k) => ({
        id: k.skuId,
        label: k.name || k.skuId,
        subtitle: `Offer · ${k.saleMode || 'sale'}${k.amountCents ? ` · ${money(k.amountCents)}` : ''}`,
        imageUrl: undefined,
      }));
      return [
        ...modelRows.sort((a, b) => a.label.localeCompare(b.label)),
        ...bundleRows.sort((a, b) => a.label.localeCompare(b.label)),
        ...skuRows.sort((a, b) => a.label.localeCompare(b.label)),
      ];
    }
    return [];
  };

  /** Ids another rule in the same scope already claims (primary or extra). */
  const claimedBy = (scope: CommissionRuleRow['scope'], allRules: CommissionRuleRow[], self: CommissionRuleRow) => {
    const out = new Set<string>();
    for (const r of allRules) {
      if (r.scope !== scope || r.ruleId === self.ruleId) continue;
      for (const t of targetsOf(r as any)) if (t !== ANY) out.add(t);
    }
    return out;
  };

  /** Clipboard can be blocked (insecure origin, permissions) — never throw. */
  const copyId = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
      setNotice(`Copied ${id}`);
    } catch {
      setError(`Could not copy automatically — the id is ${id}`);
    }
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const body: Record<string, any> = {
        name: selected.name,
        description: selected.description,
        status: selected.status,
        isDefault: selected.isDefault,
        rules: selected.rules,
      };
      // A new plan sends no planId — the server mints a readable one from the
      // name. Sending '' would create a row keyed on an empty string.
      if (selected.planId) body.planId = selected.planId;

      const res = await billingApi.upsertCommissionPlan(body);
      setNotice(`Saved "${res.plan.name}" — version ${res.plan.version}`);
      setDraft(null);
      setPlan(res.plan);
      if (isNew || res.plan.planId !== routePlanId) {
        // The URL becomes the plan's own id the moment it has one, so a
        // freshly-created plan is bookmarkable and reloadable immediately.
        navigate(`/commission-plans/${encodeURIComponent(res.plan.planId)}`, { replace: true });
      }
    } catch (e: any) {
      // The server's rules message (margin basis on a rental, duplicate
      // target, floor above cap) is shown VERBATIM — it names the exact rule.
      setError(e?.message || 'Could not save the plan');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!selected?.planId) return;
    setSaving(true);
    setError('');
    try {
      await billingApi.deleteCommissionPlan(selected.planId);
      navigate('/commission-plans');
    } catch (e: any) {
      setError(e?.message || 'Could not delete the plan');
      setSaving(false);
    }
  };

  return (
    <InventoryPage
      icon={Percent}
      title={isNew ? 'New commission plan' : (plan?.name || 'Commission plan')}
      subtitle="Rates are edited here and nowhere else. A deal points at this plan; a partner can be pinned to it."
      actions={(
        <button
          onClick={() => navigate('/commission-plans')}
          className="inline-flex items-center gap-1.5 rounded-lg border border-white/25 px-3 py-2 text-xs font-semibold text-white hover:bg-white/10"
        >
          <ArrowLeft size={14} /> All plans
        </button>
      )}
    >
      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-700">
          <AlertTriangle size={15} className="mt-0.5 shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="shrink-0 text-red-400 hover:text-red-700"><X size={13} /></button>
        </div>
      )}
      {notice && (
        <div className="mb-4 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-700">
          <Check size={15} /> <span className="flex-1">{notice}</span>
          <button onClick={() => setNotice('')} className="text-emerald-500 hover:text-emerald-800"><X size={13} /></button>
        </div>
      )}

      {loading ? (
        <RubyLoader variant="section" label="Loading plan…" className="rounded-xl border border-stone-200 bg-white" />
      ) : !selected ? (
        <div className="rounded-xl border border-stone-200 bg-white p-10 text-center text-sm text-stone-500">
          This plan could not be loaded.
        </div>
      ) : (
        <div className="mx-auto max-w-4xl space-y-4">
          <div className="rounded-xl border border-stone-200 bg-white p-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-[220px] flex-1">
                <label className="mb-1 block text-[11px] font-semibold text-stone-600">Plan name</label>
                <input
                  value={selected.name}
                  onChange={(e) => patchDraft({ name: e.target.value })}
                  placeholder="Gold Partner"
                  className="w-full rounded-lg border border-stone-300 px-2.5 py-1.5 text-sm outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100"
                />
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-semibold text-stone-600">Status</label>
                <div className="w-56">
                  <SearchableDropdown
                    options={[
                      { value: 'draft', label: 'Draft', subtitle: 'Editable, not yet paying anyone' },
                      { value: 'active', label: 'Active', subtitle: 'Live — rules here decide real payouts' },
                      { value: 'retired', label: 'Retired', subtitle: 'Hidden from pickers; existing events keep their history' },
                    ]}
                    value={selected.status}
                    onChange={(v) => v && patchDraft({
                      status: v as CommissionPlanRow['status'],
                      // THE DEFAULT IS ALWAYS PUBLISHED. Moving the global
                      // fallback back to Draft is the exact bug this gate
                      // exists to stop (a draft "temp plan" was found pricing
                      // every commission on the platform), so the two controls
                      // move together instead of silently contradicting each
                      // other. The server enforces the same rule regardless —
                      // this only keeps the screen honest about it.
                      ...(v !== 'active' && selected.isDefault ? { isDefault: false } : {}),
                    })}
                    placeholder="Status"
                    variant="light"
                    compact
                  />
                </div>
                {selected.isDefault && (
                  <p className="mt-1 text-[10px] text-stone-400">
                    The global default is always Active.
                  </p>
                )}
              </div>
              <label className="flex items-center gap-1.5 pb-2 text-xs text-stone-600">
                <input
                  type="checkbox"
                  checked={selected.isDefault}
                  // Ticking this PUBLISHES the plan: the fallback every
                  // unpinned sale pays against cannot be an unfinished draft.
                  onChange={(e) => patchDraft(e.target.checked
                    ? { isDefault: true, status: 'active' }
                    : { isDefault: false })}
                  className="h-3.5 w-3.5 accent-rose-500"
                />
                Global default
                {!selected.isDefault && selected.status !== 'active' && (
                  <span className="text-[10px] text-stone-400">(publishes it)</span>
                )}
              </label>
              <div className="pb-2 text-right">
                <div className="font-mono text-[11px] text-stone-400">v{selected.version}</div>
                {selected.planId ? (
                  <button
                    onClick={() => copyId(selected.planId)}
                    title="Copy plan id — every commission event records this"
                    className="mt-0.5 inline-flex items-center gap-1 rounded-md border border-stone-300 bg-stone-50 px-2 py-1 font-mono text-[11px] text-stone-700 hover:border-rose-300 hover:text-rose-600"
                  >
                    {selected.planId}
                    <Copy size={11} className="opacity-60" />
                  </button>
                ) : (
                  <div className="mt-0.5 font-mono text-[11px] text-stone-500">
                    {mintedIdPreview
                      ? <span className="rounded-md border border-dashed border-stone-300 bg-stone-50 px-2 py-1">{mintedIdPreview}</span>
                      : <span className="text-stone-400">id follows the name</span>}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-3">
              <label className="mb-1 block text-[11px] font-semibold text-stone-600">Description</label>
              <input
                value={selected.description || ''}
                onChange={(e) => patchDraft({ description: e.target.value })}
                placeholder="Who this is for, and why it pays what it pays."
                className="w-full rounded-lg border border-stone-300 px-2.5 py-1.5 text-sm outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100"
              />
            </div>

            <p className="mt-2 text-[10px] text-stone-400">
              The plan id is minted from the name and then <strong>frozen for ever</strong> — it is stamped
              on every commission event this plan pays, so renaming the plan later never changes it.
              The default plan is what applies when a deal names none; exactly one plan holds it, and
              ticking this here demotes the previous one automatically.
            </p>
          </div>

          {SCOPES.map((sc) => {
            const rules = selected.rules.filter((r) => r.scope === sc.id);
            return (
              <div key={sc.id} className="rounded-xl border border-stone-200 bg-white p-4">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <div>
                    <h3 className="text-xs font-bold text-stone-900">{sc.label}</h3>
                    <p className="text-[10px] text-stone-400">{sc.hint}</p>
                  </div>
                  <button
                    onClick={() => addRule(sc.id)}
                    className="rounded-lg border border-stone-300 px-2.5 py-1.5 text-[11px] font-semibold text-stone-600 hover:border-stone-400"
                  >
                    + Rule
                  </button>
                </div>

                {rules.length === 0 ? (
                  <p className="rounded-lg border border-dashed border-stone-200 p-3 text-[11px] text-stone-400">
                    No rule — nothing in this category pays a partner.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {rules.map((r) => (
                      <div key={r.ruleId} className="rounded-lg border border-stone-200 bg-stone-50/60 p-2.5">
                        {/* Targets get their OWN full-width row: package and
                            device names are long, and a truncated name is how
                            a commission ends up on the wrong thing. */}
                        <TargetPicker
                          choices={choicesFor(sc.id)}
                          selected={targetsOf(r as any)}
                          claimed={claimedBy(sc.id, selected.rules, r)}
                          catchAllLabel="Everything"
                          onChange={(ids) => {
                            const specific = ids.filter((x) => x !== ANY);
                            patchRule(r.ruleId, specific.length === 0
                              ? { target: ANY, targets: undefined }
                              : { target: specific[0], targets: specific.slice(1) });
                          }}
                        />
                        {targetsOf(r as any).some((t) => t !== ANY) && (
                          <p className="mt-1 text-[10px] text-emerald-600">
                            These are taken out of the catch-all — an exact match always wins.
                          </p>
                        )}

                        <div className="mt-2 grid grid-cols-2 gap-2 lg:grid-cols-4">
                          <div>
                            <label className="mb-1 block text-[10px] font-semibold text-stone-500">Basis</label>
                            <SearchableDropdown
                              /* Margin basis is offered only where it can mean something —
                                 the server refuses it elsewhere, and on rental targets. */
                              options={[
                                { value: 'percent_of_price', label: BASIS_LABEL.percent_of_price, subtitle: 'Of what the customer pays' },
                                ...(sc.id === 'device_offer'
                                  ? [{ value: 'percent_of_margin', label: BASIS_LABEL.percent_of_margin, subtitle: 'Of (price − landed cost) — never pays more than the sale earned' }]
                                  : []),
                                { value: 'flat_per_unit', label: BASIS_LABEL.flat_per_unit, subtitle: 'A fixed amount, whatever the price' },
                              ]}
                              value={r.basis}
                              onChange={(v) => v && patchRule(r.ruleId, { basis: v as CommissionRuleRow['basis'] })}
                              placeholder="Basis"
                              variant="light"
                              compact
                            />
                          </div>
                          <div>
                            <label className="mb-1 block text-[10px] font-semibold text-stone-500">
                              {r.basis === 'flat_per_unit' ? 'Amount ($)' : 'Rate (%)'}
                            </label>
                            {r.basis === 'flat_per_unit' ? (
                              <input
                                value={centsToStr(r.flatCents)}
                                onChange={(e) => patchRule(r.ruleId, { flatCents: strToCents(e.target.value) })}
                                className="w-full rounded-lg border border-stone-300 px-2 py-1.5 text-right font-mono text-xs"
                              />
                            ) : (
                              <input
                                type="number" min="0" max="100" step="0.5"
                                value={r.rate}
                                onChange={(e) => patchRule(r.ruleId, { rate: Number(e.target.value) })}
                                className="w-full rounded-lg border border-stone-300 px-2 py-1.5 text-right font-mono text-xs"
                              />
                            )}
                          </div>
                          <div>
                            <label className="mb-1 block text-[10px] font-semibold text-stone-500">Pays</label>
                            <SearchableDropdown
                              options={[
                                { value: 'once', label: 'Once', subtitle: 'A single payment when the sale settles' },
                                { value: '6', label: 'Monthly · 6 mo', subtitle: 'Then stops' },
                                { value: '12', label: 'Monthly · 12 mo', subtitle: 'Then stops' },
                                { value: '24', label: 'Monthly · 24 mo', subtitle: 'Then stops' },
                                { value: 'life', label: 'Monthly · for life', subtitle: 'For as long as the subscription lives' },
                              ]}
                              value={r.recurrence === 'once' ? 'once' : String(r.months ?? 'life')}
                              onChange={(v) => {
                                if (!v) return;
                                if (v === 'once') patchRule(r.ruleId, { recurrence: 'once', months: null });
                                else if (v === 'life') patchRule(r.ruleId, { recurrence: 'monthly', months: null });
                                else patchRule(r.ruleId, { recurrence: 'monthly', months: Number(v) });
                              }}
                              placeholder="Pays"
                              variant="light"
                              compact
                            />
                          </div>
                          <div className="flex items-end justify-end">
                            <button
                              onClick={() => removeRule(r.ruleId)}
                              className="rounded-lg border border-stone-200 p-1.5 text-stone-400 hover:border-red-300 hover:text-red-500"
                              title="Remove this rule"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </div>
                        {r.basis === 'flat_per_unit' && r.flatCents > 0 && (
                          <p className="mt-1.5 text-[10px] text-stone-400">
                            Pays {money(r.flatCents)} per unit, {r.recurrence === 'once' ? 'once' : 'every cycle'}.
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* A rule in a scope this editor no longer offers still PAYS. Show it,
              with a way to delete it — never leave money moving invisibly. */}
          {selected.rules.some((r) => RETIRED_SCOPES.includes(r.scope)) && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle size={15} className="mt-0.5 shrink-0 text-amber-600" />
                <div className="min-w-0 flex-1">
                  <h3 className="text-xs font-bold text-amber-900">Rules this editor no longer offers</h3>
                  <p className="mt-1 text-[11px] text-amber-800">
                    These still pay. A deal's monthly price is the sum of its packages, so a
                    subscription rule alongside package rules pays twice on the same money.
                    Delete them unless you know you want both.
                  </p>
                  <div className="mt-2 space-y-1.5">
                    {selected.rules.filter((r) => RETIRED_SCOPES.includes(r.scope)).map((r) => (
                      <div key={r.ruleId} className="flex items-center gap-2 rounded-lg border border-amber-200 bg-white px-2.5 py-1.5">
                        <span className="flex-1 truncate text-[11px] text-stone-700">
                          <span className="font-semibold">{r.scope.replace('_', ' ')}</span>
                          {' · '}
                          {r.basis === 'flat_per_unit' ? `${money(r.flatCents)} flat` : `${r.rate}%`}
                          {r.recurrence === 'monthly' ? ` · monthly${r.months ? ` ${r.months}mo` : ' for life'}` : ' · once'}
                        </span>
                        <button
                          onClick={() => removeRule(r.ruleId)}
                          className="rounded-lg border border-amber-300 p-1 text-amber-700 hover:bg-amber-100"
                          title="Remove this rule"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="sticky bottom-0 flex items-center gap-2 rounded-xl border border-stone-200 bg-white/95 p-3 backdrop-blur">
            <button
              onClick={save}
              disabled={saving || !selected.name.trim()}
              className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-xs font-semibold text-white disabled:opacity-50"
              style={{ background: `linear-gradient(135deg, ${RUBY}, ${RUBY_DEEP})` }}
            >
              {saving ? <RubyGemMark size={14} drawing color="currentColor" /> : <Check size={14} />}
              {isNew ? 'Create plan' : 'Save plan'}
            </button>
            {dirty && (
              <button
                onClick={() => setDraft(null)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-stone-300 px-3 py-2 text-xs font-semibold text-stone-600"
              >
                <X size={13} /> Discard changes
              </button>
            )}
            {!selected.name.trim() && (
              <span className="text-[11px] text-stone-400">A plan needs a name — its id is minted from it.</span>
            )}
            {/* The default plan cannot be deleted: without it a partner whose
                deal names no plan would silently earn zero. */}
            {selected.planId && !selected.isDefault && (
              <button
                onClick={remove}
                disabled={saving}
                className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50"
              >
                <Trash2 size={13} /> Delete
              </button>
            )}
          </div>
        </div>
      )}
    </InventoryPage>
  );
};

export default CommissionPlanEditPage;
